const RETRY_COOLDOWN_MS = 30000
const DEFAULT_TICKET_FRESH_MS = 5 * 60 * 60000
const FORCE_GRACE_MS = 5000
const RETRY_PARAM = 'afsRetry'

const env = () => window.__env__ || {}

const ticketFreshMs = () => {
  const raw = Number(env().REACT_APP_FILE_TICKET_FRESH_MS)
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_TICKET_FRESH_MS
}

let inFlight = null
let blockedUntil = 0
let freshUntil = 0
let mintedAt = 0
let listenerInstalled = false

const sessionUrl = () => {
  const base = env().REACT_APP_FILE_URL
  return base ? `${base}/session` : null
}

export const ensureFileTicket = (options) => {
  if (inFlight) return inFlight
  if (Date.now() - mintedAt < FORCE_GRACE_MS) return Promise.resolve(true)
  if (!options?.force && Date.now() < freshUntil) return Promise.resolve(true)
  if (Date.now() < blockedUntil) return Promise.resolve(false)

  const url = sessionUrl()
  const token = localStorage.getItem('token')
  if (!url || !token) return Promise.resolve(false)

  inFlight = fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { Authorization: token },
  })
    .then((res) => {
      if (res.ok) {
        mintedAt = Date.now()
        freshUntil = mintedAt + ticketFreshMs()
      } else {
        blockedUntil = Date.now() + RETRY_COOLDOWN_MS
      }
      return res.ok
    })
    .catch(() => {
      blockedUntil = Date.now() + RETRY_COOLDOWN_MS
      return false
    })
    .finally(() => {
      inFlight = null
    })

  return inFlight
}

export const clearFileTicket = () => {
  blockedUntil = 0
  freshUntil = 0
  mintedAt = 0
  const url = sessionUrl()
  if (!url) return Promise.resolve()

  return fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    keepalive: true,
  })
    .then(() => undefined)
    .catch(() => undefined)
}

const toAbsolute = (url) => {
  try {
    return new URL(url, window.location.href).toString()
  } catch (error) {
    return null
  }
}

const isTicketedFile = (src) => {
  const target = src && toAbsolute(src)
  if (!target) return false

  const { REACT_APP_FILE_URL, REACT_APP_API_URL } = env()
  const bases = [
    REACT_APP_FILE_URL,
    REACT_APP_API_URL ? `${REACT_APP_API_URL}/file` : null,
  ]

  return bases.some((base) => {
    const prefix = base && toAbsolute(base)
    return !!prefix && target.startsWith(prefix)
  })
}

export const retryTicketedImageSrc = async (src) => {
  if (!src || !isTicketedFile(src)) return null
  if (!(await ensureFileTicket({ force: true }))) return null

  const retried = new URL(src, window.location.href)
  retried.searchParams.set(RETRY_PARAM, String(Date.now()))
  return retried.toString()
}

const handleImageError = (event) => {
  const image = event.target
  if (!image || image.tagName !== 'IMG') return
  if (image.dataset.afsRetried) return
  if (!isTicketedFile(image.src)) return

  image.dataset.afsRetried = '1'
  void retryTicketedImageSrc(image.src).then((next) => {
    if (next) image.src = next
  })
}

export const installTicketedImageRetry = () => {
  if (listenerInstalled) return
  listenerInstalled = true
  document.addEventListener('error', handleImageError, true)
}

export const resetFileTicketStateForTest = () => {
  inFlight = null
  blockedUntil = 0
  freshUntil = 0
  mintedAt = 0
  if (listenerInstalled) {
    document.removeEventListener('error', handleImageError, true)
    listenerInstalled = false
  }
}
